/**
 * The "Föreläsning" tab of `/library/topics` (slice B, P1-2): F01–F12 and R01–R03 in date order,
 * each with its date, state (genomgången / idag / nästa / kommande), title, the Y&F sections
 * behind its topics, the topic names and an "Öva" link to the lecture setup page
 * (`/practice?course=HT2026&lecture=F01`, which still waits for "Starta"). The data is the
 * public course catalogue and the shared topics query; the state is the Europe/Stockholm day.
 */
import { Link } from "react-router";
import clsx from "clsx";
import type { PublicLecture } from "@content/courseSchema";
import { useTopicsAndChapters } from "@client/api/topics";
import { useLocalizedText } from "@client/components/Localized";
import { Badge, Card, EmptyState, Spinner } from "@client/components/ui";
import { useLang, useT } from "@client/i18n";
import { formatDayShort, formatList } from "@client/lib/format";
import { useIsTeacher } from "@client/app/auth";
import {
  canPractise,
  isUnreleased,
  lecturePracticeHref,
  lectureSections,
  releaseText,
  useLectureTimeline,
} from "./course";

type RowState = "done" | "today" | "next" | "upcoming";

const STATE_TONE = {
  done: "neutral",
  today: "accent",
  next: "accent",
  upcoming: "neutral",
} as const;

const PRACTISE_LINK =
  "inline-flex h-8 shrink-0 items-center justify-center rounded-lg border border-ink-300 bg-white px-3 text-sm font-medium text-ink-800 transition-colors hover:bg-ink-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-500 dark:border-ink-700 dark:bg-ink-900 dark:text-ink-100 dark:hover:bg-ink-800";

function LectureRow({ lecture, state }: { lecture: PublicLecture; state: RowState }) {
  const t = useT();
  const { lang } = useLang();
  const localize = useLocalizedText();
  const topics = useTopicsAndChapters();
  const teacher = useIsTeacher();
  const topicOf = (id: string) => topics.data?.topics.find((topic) => topic.id === id);
  const sections = lectureSections(lecture.topics, (id) => topicOf(id)?.book);
  const names = lecture.topics.map((id) => {
    const topic = topicOf(id);
    return topic ? localize(topic.name) : id;
  });

  return (
    <li
      className={clsx(
        "flex flex-col gap-3 border-b border-ink-100 py-3 last:border-0 dark:border-ink-900 sm:flex-row sm:items-start sm:justify-between",
        state === "done" && "opacity-80",
      )}
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-medium text-ink-800 dark:text-ink-100">
            {lecture.id} · {localize(lecture.title)}
          </p>
          <Badge tone={STATE_TONE[state]}>{t(`content.lecture.state.${state}`)}</Badge>
        </div>
        <p className="mt-1 text-sm text-ink-600 dark:text-ink-400">
          {[
            formatDayShort(lecture.date, lang),
            sections.length > 0
              ? t("content.lecture.reading", { sections: sections.join(", ") })
              : null,
          ]
            .filter((part): part is string => part !== null)
            .join(" · ")}
        </p>
        <p className="mt-1 text-sm text-ink-600 dark:text-ink-400">
          {t("content.lecture.topics", { topics: formatList(names, lang) })}
        </p>
      </div>
      {canPractise(lecture, teacher) ? (
        <Link
          to={lecturePracticeHref(lecture.id)}
          className={PRACTISE_LINK}
          aria-label={t("content.lecture.practiseLabel", { id: lecture.id })}
        >
          {t("content.lecture.practise")}
        </Link>
      ) : (
        <span className="shrink-0 text-sm text-ink-500 dark:text-ink-400">
          {isUnreleased(lecture, teacher)
            ? releaseText(lecture.release, t, lang)
            : t("content.lecture.unavailable")}
        </span>
      )}
    </li>
  );
}

export function LecturesView() {
  const t = useT();
  const { timeline, isLoading, isError } = useLectureTimeline();

  if (isLoading)
    return (
      <div className="flex justify-center py-8">
        <Spinner size="lg" label={t("common.loading")} />
      </div>
    );
  if (isError || !timeline) return <EmptyState title={t("content.topics.lectures.empty")} />;

  const stateOf = (lecture: PublicLecture): RowState =>
    timeline.done.includes(lecture)
      ? "done"
      : lecture === timeline.today
        ? "today"
        : lecture === timeline.focus
          ? "next"
          : "upcoming";

  return (
    <Card compact>
      <p className="mb-2 text-sm text-ink-600 dark:text-ink-400">
        {t("content.topics.lectures.intro")}
      </p>
      <ul>
        {timeline.ordered.map((lecture) => (
          <LectureRow key={lecture.id} lecture={lecture} state={stateOf(lecture)} />
        ))}
      </ul>
    </Card>
  );
}
